import Dexie, { type Table } from 'dexie';
import type { Session } from '@/types/session';
import type { Opportunity } from '@/types/opportunity';
import type { HCWCategory } from '@/types/hcw';

export class CleanHandsDB extends Dexie {
  sessions!: Table<Session, string>;
  opportunities!: Table<Opportunity, string>;
  hcw_categories!: Table<HCWCategory, string>;

  constructor() {
    super('CleanHandsDB');

    this.version(1).stores({
      sessions:
        '&sessionId, sessionDate, status, wardName, periodNumber, [sessionDate+wardName]',
      opportunities:
        '&opportunityId, sessionId, timestamp, [sessionId+opportunityNumber]',
      hcw_categories: '&code, parentCategory, sortOrder',
    });

    // Version 2: Add healthworker tracking fields to opportunities
    this.version(2)
      .stores({
        sessions:
          '&sessionId, sessionDate, status, wardName, periodNumber, [sessionDate+wardName]',
        opportunities:
          '&opportunityId, sessionId, timestamp, [sessionId+opportunityNumber]',
        hcw_categories: '&code, parentCategory, sortOrder',
      })
      .upgrade((tx) => {
        return tx
          .table('opportunities')
          .toCollection()
          .modify((opp) => {
            if (!opp.healthworkerIdentifier) {
              opp.healthworkerIdentifier = 'star_1';
              opp.healthworkerColor = 'red';
            }
          });
      });

    // Version 3: Add bilingual name fields (name_en, name_es) to hcw_categories
    const SPANISH_NAMES: Record<string, string> = {
      '1.1': 'Enfermera',
      '1.2': 'Partera',
      '1.3': 'Estudiante de Enfermería',
      '2.1': 'Auxiliar de Enfermería',
      '2.2': 'Asistente de Cuidado al Paciente',
      '3.1': 'Médico (Medicina Interna)',
      '3.2': 'Cirujano',
      '3.3': 'Anestesiólogo',
      '3.4': 'Pediatra',
      '3.5': 'Estudiante de Medicina',
      '4.1': 'Fisioterapeuta',
      '4.2': 'Radiógrafo / Técnico',
      '4.3': 'Nutricionista',
      '5.1': 'Otro Trabajador de Salud',
    };

    this.version(3)
      .stores({
        sessions:
          '&sessionId, sessionDate, status, wardName, periodNumber, [sessionDate+wardName]',
        opportunities:
          '&opportunityId, sessionId, timestamp, [sessionId+opportunityNumber]',
        hcw_categories: '&code, parentCategory, sortOrder',
      })
      .upgrade((tx) => {
        return tx
          .table('hcw_categories')
          .toCollection()
          .modify((cat) => {
            if (!cat.name_en) {
              cat.name_en = cat.name;
              cat.name_es = SPANISH_NAMES[cat.code] || cat.name;
            }
          });
      });
  }
}

export const db = new CleanHandsDB();
